/**
 * Achievement - Toast card that slides in from the top-right. Displays a 64×64
 * icon, title, and description with a rarity-coded border. Auto-hides after a
 * configurable duration.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface AchievementShowOptions {
  /** Heading text. */
  title: string;
  /** Supporting description. */
  description?: string;
  /** SVG markup for the 64×64 icon slot. */
  iconHtml?: string;
  /** Rarity tier controlling the border colour. */
  rarity?: AchievementRarity;
  /** Auto-hide delay in ms. Defaults to 4000. */
  duration?: number;
}

export interface AchievementOptions extends BaseUIComponentOptions {}

export class Achievement extends BaseUIComponent<AchievementOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Achievement',
    category: 'feedback',
    summary: 'Toast that slides in from top-right with icon, title, description, and rarity border.',
    useCases: ['achievement', 'toast', 'unlock', 'notification', 'reward', 'badge unlock'],
    optionsType: 'AchievementOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/achievement.html',
      styles: '@engine/assets/ui/styles/achievement.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private iconElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private descriptionElement: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private currentRarity: AchievementRarity = 'common';

  protected override getAssetPaths() {
    return {
      templatePath: Achievement.metadata.assetPaths.template,
      stylesPath: Achievement.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<AchievementOptions> {
    return {
      position: 'top-right',
      visible: false,
      customClasses: [],
      customStyles: {},
    };
  }

  protected override getInitialData(): Record<string, any> {
    return { title: '', description: '', rarity: 'common' };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-achievement') as HTMLElement | null;
    this.iconElement = this.layout.querySelector('[data-achievement-icon]') as HTMLElement | null;
    this.titleElement = this.layout.querySelector('[data-achievement-title]') as HTMLElement | null;
    this.descriptionElement = this.layout.querySelector('[data-achievement-description]') as HTMLElement | null;
  }

  /**
   * Display the achievement toast with the given content. Starts the auto-hide
   * timer unless `duration` is 0.
   */
  public override show(opts?: AchievementShowOptions): void {
    if (!opts) return;
    this.clearHideTimer();
    const { title, description = '', iconHtml = '', rarity = 'common', duration = 4000 } = opts;

    // Update rarity class
    if (this.rootElement) {
      this.rootElement.classList.remove(`rarity-${this.currentRarity}`);
      this.currentRarity = rarity;
      this.rootElement.classList.add(`rarity-${rarity}`);
    }

    if (this.titleElement) this.titleElement.textContent = title;
    if (this.descriptionElement) this.descriptionElement.textContent = description;
    if (this.iconElement) this.iconElement.innerHTML = iconHtml;

    if (this.rootElement) this.rootElement.dataset.visible = 'true';
    this.isVisible = true;

    if (duration > 0) {
      this.hideTimer = setTimeout(() => this.hide(), duration);
    }
  }

  /** Slide the toast out immediately. */
  public override hide(): void {
    this.clearHideTimer();
    if (this.rootElement) this.rootElement.dataset.visible = 'false';
    this.isVisible = false;
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  protected override onDestroy(): void {
    this.clearHideTimer();
    this.rootElement = null;
    this.iconElement = null;
    this.titleElement = null;
    this.descriptionElement = null;
  }
}
